import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Plus, ArrowRight } from "lucide-react";
import { useApp, formatEUR } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export function ProductCard({ product, index = 0 }) {
  const { t, lang, addToCart, setCartOpen } = useApp();

  const unitLabel =
    product.unit === "day" ? t.perDay : product.unit === "forfait" ? ` ${t.forfait}` : ` ${t.unitPrice}`;

  const handleAdd = (e) => {
    e.preventDefault();
    e.stopPropagation();
    addToCart(product.id, 1);
    toast.success(`${product.name[lang]} — ${t.cta.added}`, {
      action: { label: t.cart.title, onClick: () => setCartOpen(true) },
    });
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.4, delay: Math.min(index * 0.05, 0.3) }}
    >
      <Link
        to={`/produit/${product.id}`}
        data-testid={`product-card-${product.id}`}
        className="group flex h-full flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-blue-900/5 focus:outline-none focus:ring-2 focus:ring-cea-accent"
      >
        <div className="relative aspect-[4/3] overflow-hidden bg-slate-100">
          <img
            src={product.images[0]}
            alt={product.name[lang]}
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        </div>
        <div className="flex flex-1 flex-col p-5">
          <h3 className="text-base font-bold text-cea-ink">{product.name[lang]}</h3>
          <p className="mt-1 line-clamp-2 text-sm text-slate-500">{product.tagline[lang]}</p>
          <span className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-cea-accent">
            {t.detail.learnMore}<ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
          </span>
          <div className="mt-4 flex items-end justify-between pt-2">
            <p className="text-lg font-extrabold text-cea-navy" data-testid={`price-${product.id}`}>
              {formatEUR(product.price)}
              <span className="text-xs font-medium text-slate-400">{unitLabel}</span>
            </p>
            <Button
              onClick={handleAdd}
              size="sm"
              data-testid={`add-to-cart-${product.id}`}
              className="rounded-full bg-cea-accent text-white hover:bg-cea-navy"
            >
              <Plus className="mr-1 h-4 w-4" />
              {product.unit === "day" ? t.cta.rent : t.cta.addToCart}
            </Button>
          </div>
        </div>
      </Link>
    </motion.div>
  );
}
